import { ArrowDownTrayIcon, ArrowPathIcon, ClockIcon, DocumentTextIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { DocumentAsset, DocumentJob } from "@xhalo/document-contracts";
import { documentApiClient, DocumentApiError } from "./cloud-document-client";
import { toolByOperation } from "./tool-catalog";
import { useDocumentStore } from "./store";
import { localSignInHref } from "./auth-client";
import { formatBytes, formatDate } from "./format";

const activeStates = new Set(["queued", "claimed", "running", "cancel_requested", "failed"]);

function isAccessError(error: unknown): boolean {
  return error instanceof DocumentApiError && (error.code === "AUTH_REQUIRED" || error.code === "BETA_ACCESS_REQUIRED");
}

function retryQuery(failureCount: number, error: unknown): boolean {
  return !isAccessError(error) && failureCount < 2;
}

function resultFilename(job: DocumentJob): string {
  const mime = job.outputs?.[0]?.mimeType;
  const extension = mime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ? "docx"
    : mime === "application/zip"
      ? "zip"
      : mime === "image/png"
        ? "png"
        : mime === "image/jpeg"
          ? "jpg"
          : "pdf";
  return `xhalo-${job.operation}.${extension}`;
}

function mimeBadge(mime: string): string {
  if (mime === "application/pdf") return "PDF";
  if (mime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") return "DOCX";
  if (mime === "application/zip") return "ZIP";
  if (mime === "image/png") return "PNG";
  if (mime === "image/jpeg") return "JPEG";
  return "FILE";
}

function SignInAction() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}${location.hash}`;
  return <a className="primary-button library-sign-in" href={localSignInHref(returnTo, i18n.language)}>{t("pages.signIn")}</a>;
}

function DownloadAssetAction({ assetId, filename }: { assetId: string; filename: string }) {
  const { t } = useTranslation();
  const download = useMutation({
    mutationFn: () => documentApiClient.createDownloadUrl(assetId),
    onSuccess: (url) => {
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      anchor.rel = "noreferrer";
      anchor.click();
    },
  });
  return <>
    <button className="library-action" type="button" onClick={() => download.mutate()} disabled={download.isPending}>
      {download.isPending ? <ArrowPathIcon className="spin" /> : <ArrowDownTrayIcon />}
      <span>{download.isPending ? t("pages.preparingDownload") : t("pages.download")}</span>
    </button>
    {download.isError ? <span className="library-inline-error" role="alert">{t("workspace.downloadError")}</span> : null}
  </>;
}

function FileRow({ asset }: { asset: DocumentAsset }) {
  const { t, i18n } = useTranslation();
  const setPendingCloudAssets = useDocumentStore((state) => state.setPendingCloudAssets);
  const filename = asset.filename ?? `${asset.assetId}.pdf`;
  return <li className="library-row" data-clarity-mask="true">
    <span className="library-file-icon"><DocumentTextIcon /></span>
    <span className="library-row-main"><strong>{filename}</strong><small>{formatBytes(asset.bytes, i18n.language)} · {formatDate(asset.createdAt, i18n.language)}</small></span>
    <span className="library-mime">{mimeBadge(asset.mime)}</span>
    <DownloadAssetAction assetId={asset.assetId} filename={filename} />
    {asset.mime === "application/pdf" ? <Link className="library-action secondary" to="/tools" onClick={() => setPendingCloudAssets([{ assetId: asset.assetId, name: filename, mime: asset.mime, bytes: asset.bytes }])}>{t("pages.processAgain")}</Link> : null}
  </li>;
}

export function CloudFilesContent() {
  const { t, i18n } = useTranslation();
  const files = useQuery({ queryKey: ["document-files"], queryFn: () => documentApiClient.listFiles(), staleTime: 30_000, retry: retryQuery });
  const usage = useQuery({ queryKey: ["document-usage"], queryFn: () => documentApiClient.getUsage(), staleTime: 30_000, retry: retryQuery });
  const accessRequired = isAccessError(files.error);

  return <div className="library-page">
    <div className="section-heading"><div><h1>{t("pages.filesTitle")}</h1><p>{t("pages.filesSubtitle")}</p></div></div>
    {usage.data ? <div className="usage-strip" aria-label={t("pages.usageTitle")}>
      <span><strong>{usage.data.jobsToday}</strong>{t("pages.jobsToday")}</span>
      <span><strong>{formatBytes(usage.data.bytesToday, i18n.language)}</strong>{t("pages.bytesToday")}</span>
      <span><strong>{usage.data.concurrentJobs}</strong>{t("pages.activeJobs")}</span>
    </div> : null}
    {files.isPending ? <div className="library-state" role="status"><ArrowPathIcon className="spin" />{t("pages.loadingFiles")}</div> : null}
    {accessRequired ? <div className="library-state"><DocumentTextIcon /><strong>{t("pages.filesSignInTitle")}</strong><p>{t("pages.filesEmpty")}</p><SignInAction /></div> : null}
    {files.isError && !accessRequired ? <div className="library-state error" role="alert"><strong>{t("pages.loadError")}</strong><button type="button" onClick={() => void files.refetch()}>{t("pages.retry")}</button></div> : null}
    {files.data?.length === 0 ? <div className="library-state"><DocumentTextIcon /><strong>{t("pages.noFilesTitle")}</strong><p>{t("pages.noFilesDescription")}</p><Link className="primary-button library-sign-in" to="/">{t("pages.openTools")}</Link></div> : null}
    {files.data?.length ? <ul className="library-list">{files.data.map((asset) => <FileRow asset={asset} key={asset.assetId} />)}</ul> : null}
  </div>;
}

function JobRow({ job }: { job: DocumentJob }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const cancel = useMutation({
    mutationFn: () => documentApiClient.cancelJob(job.id),
    onSuccess: (updated) => {
      queryClient.setQueryData<readonly DocumentJob[]>(["document-jobs"], (jobs = []) => jobs.map((item) => item.id === updated.id ? updated : item));
    },
  });
  const retry = useMutation({
    mutationFn: () => documentApiClient.retryJob(job.id),
    onSuccess: (created) => {
      queryClient.setQueryData<readonly DocumentJob[]>(["document-jobs"], (jobs = []) => [created, ...jobs]);
    },
  });
  const output = job.outputs?.[0];
  const active = activeStates.has(job.status);
  const tool = toolByOperation(job.operation);

  return <li className="library-row job-row">
    <span className={`job-state-dot ${job.status}`} aria-hidden="true" />
    <span className="library-row-main"><strong>{t(`tools.${job.operation}.name`)}</strong><small>{formatDate(job.updatedAt, i18n.language)} · {t(`workspace.job.${job.status}`)}</small>{active ? <progress max="100" value={job.progress} aria-label={`${job.progress}%`} /> : null}</span>
    <span className={`job-state-label ${job.status}`}>{t(`workspace.job.${job.status}`)}</span>
    {active && job.status !== "cancel_requested" ? <button className="library-action danger" type="button" onClick={() => cancel.mutate()} disabled={cancel.isPending}><XMarkIcon />{t("pages.cancel")}</button> : null}
    {job.status === "dead_lettered" ? <button className="library-action" type="button" onClick={() => retry.mutate()} disabled={retry.isPending}><ArrowPathIcon className={retry.isPending ? "spin" : undefined} />{t("pages.retryJob")}</button> : null}
    {output ? <DownloadAssetAction assetId={output.assetId} filename={resultFilename(job)} /> : null}
    <Link className="library-action secondary" to={tool.path}>{t("pages.runAgain")}</Link>
    {retry.isError ? <span className="library-inline-error" role="alert">{t("pages.retryError")}</span> : null}
  </li>;
}

export function CloudActivityContent() {
  const { t } = useTranslation();
  const jobs = useQuery({
    queryKey: ["document-jobs"],
    queryFn: () => documentApiClient.listJobs(),
    retry: retryQuery,
    refetchInterval: (query) => query.state.data?.some((job) => activeStates.has(job.status)) ? 5_000 : false,
  });
  const accessRequired = isAccessError(jobs.error);

  return <div className="library-page">
    <div className="section-heading"><div><h1>{t("pages.activityTitle")}</h1><p>{t("pages.activitySubtitle")}</p></div></div>
    {jobs.isPending ? <div className="library-state" role="status"><ArrowPathIcon className="spin" />{t("pages.loadingActivity")}</div> : null}
    {accessRequired ? <div className="library-state"><ClockIcon /><strong>{t("pages.activitySignInTitle")}</strong><p>{t("pages.activitySignInDescription")}</p><SignInAction /></div> : null}
    {jobs.isError && !accessRequired ? <div className="library-state error" role="alert"><strong>{t("pages.loadError")}</strong><button type="button" onClick={() => void jobs.refetch()}>{t("pages.retry")}</button></div> : null}
    {jobs.data?.length === 0 ? <div className="library-state"><ClockIcon /><strong>{t("pages.activityEmpty")}</strong><p>{t("pages.noActivityDescription")}</p><Link className="primary-button library-sign-in" to="/">{t("pages.openTools")}</Link></div> : null}
    {jobs.data?.length ? <ul className="library-list">{jobs.data.map((job) => <JobRow job={job} key={job.id} />)}</ul> : null}
  </div>;
}
